import type { CGU, CreateUserDto, PublicUser, User } from '@cibli/shared';

import { t } from 'i18next';

import type { UserInfo } from '@/types/user-info';

import api, { ApiOptions } from './api';

export const createUser = async (
  userInfo: UserInfo,
  { showErrorToast = true }: ApiOptions = {},
): Promise<User> => {
  const latestCGU = await api.get<CGU>('/cgus/latest');

  if (!userInfo.hasAcceptedCGU) {
    throw new Error(t('formErrors.hasAcceptedCGU'));
  }

  if (userInfo.cguId !== latestCGU.id) {
    throw new Error(t('errors.cgu_outdated'));
  }

  const userData: CreateUserDto = {
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    phoneNumber: userInfo.phone,
    email: userInfo.email && userInfo.email.trim() !== '' ? userInfo.email : undefined,
    acceptedCGU: userInfo.hasAcceptedCGU,
    cguId: userInfo.cguId,
  };

  return await api.post<User, CreateUserDto>('/users', userData, { showErrorToast });
};

/**
 * Create user from DTO (no CGU validation). Used when sign-up is handled elsewhere.
 */
export const createUserFromDto = async (
  payload: CreateUserDto,
  { showErrorToast = false }: ApiOptions = {},
): Promise<void> => {
  await api.post<void, CreateUserDto>('/users', payload, { showErrorToast });
};

/**
 * Check if a user exists by phone number or email
 * @param identifier The phone number or email to check
 * @param isEmail Whether the identifier is an email
 * @returns Object indicating if user exists
 */
export const checkUserExists = async (
  identifier: string,
  isEmail: boolean,
  { showErrorToast = false }: ApiOptions = {},
): Promise<{ exists: boolean }> => {
  const params = new URLSearchParams({
    isEmail: isEmail.toString(),
  });
  return await api.get<{ exists: boolean }>(
    `/users/exists/${encodeURIComponent(identifier)}?${params.toString()}`,
    undefined,
    { showErrorToast },
  );
};

export const getCurrentUser = async (options?: ApiOptions): Promise<PublicUser> => {
  return await api.get<PublicUser>('/users/me', undefined, options);
};

export const deleteAccount = async (options?: ApiOptions): Promise<null> => {
  await api.delete('/users/me', options);
  return null;
};

export const updateUserPreferences = async (
  preferEmailNotification: boolean,
  options?: ApiOptions,
): Promise<PublicUser> => {
  return await api.patch<PublicUser, { preferEmailNotification: boolean }>(
    '/users/me/preferences',
    { preferEmailNotification },
    options,
  );
};

export const updateUserEmail = async (
  email: string,
): Promise<{ status: 'ok'; user: { id: string; email: string } }> => {
  return await api.patch<{ status: 'ok'; user: { id: string; email: string } }, { email: string }>(
    '/users/me/email',
    { email },
  );
};

export const updateUserLocale = async (
  localeId: string,
  options?: ApiOptions,
): Promise<PublicUser> => {
  return await api.patch<PublicUser, { localeId: string }>(
    '/users/me/locale',
    { localeId },
    options,
  );
};

export default {
  createUser,
  createUserFromDto,
  checkUserExists,
  getCurrentUser,
  deleteAccount,
  updateUserPreferences,
  updateUserEmail,
  updateUserLocale,
};
